import { useMutation } from '@tanstack/react-query'
import { httpClient } from '../../../shared/api/httpClient'
import { useAuth } from '../../../shared/context/AuthContext'
import { useAstreaEnvironment } from '../../../shared/hooks/useAstreaEnvironment'

/**
 * Port of the customer-module services (`app/customer/customerService.js`,
 * `backupService.js`, `bankSlipService.js`, `devService.js`,
 * `elasticService.js`, `tenantService.js`) + the fetch/action logic of
 * `app/customer/customersController.js` (AngularJS `customersController`,
 * the controller for `customers.html`, the "Clientes" listing screen —
 * legacy state `workspace.customers`, `/workspace/customers`) to TanStack
 * Query hooks, following the same shape as `useMigrationQuery.js`/
 * `useHistoriesQuery.js` (one hook per distinct legacy service call, plain
 * `useMutation`s since the legacy controller is entirely imperative/
 * button-driven with no natural query key — same reasoning documented in
 * `useMigrationQuery.js`).
 *
 * Task 025 covers only the *listing* screen's own actions — CRUD navigation,
 * search, bounce/complaint lookup, backup, bank slips, Oroboro sync,
 * Elasticsearch reset, first-login token, return-to-trial/light. The
 * `editCustomer` (-> `workspace.customerEdit`, dead: see `CustomersListPage`'s
 * doc-comment) and `editCleanAccountPermisison` (-> `workspace.customerProfile`,
 * `CustomerService.setCleanAccountPermission`, ported below by task 026 as
 * `useCustomerProfileMutation`, consumed by `../EditCustomerProfilePage.jsx`)
 * navigation targets are owned by tasks 026-029 (sub-screens), not duplicated
 * here.
 *
 * `customersController.js` and `backupService.js` are flagged in `plan.md`
 * as touched by an in-flight Firebase-login migration on this branch
 * (`aurora/feat/000-login-via-firebase`, commit `a6c16fa`). Per this task's
 * instructions, the committed state of both files in this worktree is used
 * as-is as the porting baseline; nothing below duplicates `AuthContext`
 * (task 009) — `AuthService.getUserLogin()`/`getUrlAstrea()` map to
 * `useAuth()`'s `getUserLogin()`/`getUrlAstrea()`, already established by
 * every prior module.
 *
 * All requests below hit the *external* Astrea backend
 * (`AuthService.getUrlAstreaPath()`), same cross-origin pattern as
 * `useMigrationQuery`/`useHistoriesQuery`/`useMenemosineQuery` — confirmed
 * by reading each legacy service file: none of them call this repo's own
 * `server_app` except `saveStatusLog` (`POST /statusLogs/create`) and the
 * three `customerRouter.js`-backed endpoints below.
 *
 * Endpoints backed by THIS repo's `server_app` (`server_app/routes/
 * customerRouter.js`, confirmed by reading that file):
 * - `GET /customers/has-bounce/:email`, `GET /customers/has-complaint/:email`,
 *   `DELETE /customers/bounce/:email` (Mailgun suppression lookups).
 * - `POST /customers/change-tenant-trial`, `POST /customers/return-to-light-plan`,
 *   `POST /customers/first-login-token` (server-side proxy that attaches a
 *   short-lived "script token" via `scriptTokenMiddleware` before calling
 *   Astrea — these three are NOT called directly from the browser to Astrea,
 *   unlike every other action here).
 * - `POST /customers/pending-contracts` (server-side proxy for
 *   `/api/v2/dev/pending-contracts/tenant/{tenantId}`, also script-token
 *   backed).
 * Everything else (`getContracts`, `setCleanAccountPermission`, backup,
 * bank slips, Oroboro sync, lawsuit-link-error cleanup, Elasticsearch reset)
 * goes straight from the browser to `AuthService.getUrlAstreaPath()`.
 */

/** Port of `CustomerService.setParams(email)` + `getContracts(email)`. */
function buildContractsParams(email) {
  return email === undefined ? 'email' : `email=${email}`
}

/**
 * Port of `CustomerService.getContracts(email)`:
 *
 *   customerService.getContracts = function(email) {
 *     var params = customerService.setParams(email);
 *     return $http({ method: 'GET', url: urlAstrea + '/api/v2/contracts?' + params })
 *       .then(data => { customerService.customers = data.info; return data; })
 *       .catch(...)
 *   };
 *
 * `customersController.processRespCallback` then reads `resp.data.info`
 * (an array) and pushes every item into `$scope.contracts` — i.e. the real
 * payload lives at `response.data.info`, not `response.data` itself (the
 * legacy `.then` callback assigning `data.info` to `customerService.customers`
 * is dead code: `data` there is the raw axios/`$http`-style response, whose
 * `.info` is `undefined`; the actual list is only ever read via
 * `resp.data.info` in the controller). Reproduced faithfully: this hook
 * returns `response.data.info` (defaulting to `[]` if absent) for the page
 * to append, matching `processRespCallback`'s behavior, not the service's
 * unused `customers` field.
 */
export function useCustomerSearchMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async (email) => {
      const params = buildContractsParams(email)
      const response = await httpClient.get(`${baseUrl}/api/v2/contracts?${params}`)
      return response.data?.info ?? []
    },
  })
}

/**
 * Port of `findBounceToEmail(email)`:
 *
 *   let url = "/customers/has-bounce/" + email;
 *   $http.get(url).then(res => {
 *     $scope.bounceReason = res.data.error;
 *     $scope.hasBounce = res.data.message !== 'Address not found in bounces table';
 *   }).catch(console.error);
 *
 * Hits THIS repo's `server_app` (`GET /customers/has-bounce/:email`,
 * `customerRouter.js`), which itself proxies Mailgun's suppression API.
 * `hasBounce` is derived from a literal message-string comparison in the
 * legacy code (not an HTTP status/boolean flag) — kept as-is even though it
 * is a fragile string match, since changing it would be a behavior change,
 * not a port.
 */
export function useBounceCheckMutation() {
  return useMutation({
    mutationFn: async (email) => {
      const response = await httpClient.get(`/customers/has-bounce/${email}`)
      return {
        bounceReason: response.data?.error,
        hasBounce: response.data?.message !== 'Address not found in bounces table',
      }
    },
  })
}

/**
 * Port of `findComplaintToEmail(email)`:
 *
 *   let url = "/customers/has-complaint/" + email;
 *   $http.get(url).then(res => {
 *     $scope.hasComplaint = res.data.message !== 'No spam complaints found for this address';
 *   }).catch(console.error);
 */
export function useComplaintCheckMutation() {
  return useMutation({
    mutationFn: async (email) => {
      const response = await httpClient.get(`/customers/has-complaint/${email}`)
      return response.data?.message !== 'No spam complaints found for this address'
    },
  })
}

/**
 * Port of `unblockEmail(email)`:
 *
 *   let url = "/customers/bounce/" + email;
 *   $http.delete(url).then(res => { if (res.status === 200) $scope.hasBounce = false; });
 */
export function useUnblockEmailMutation() {
  return useMutation({
    mutationFn: async (email) => httpClient.delete(`/customers/bounce/${email}`),
  })
}

/**
 * Port of `BackupService.send(data)` + `startBackup()`:
 *
 *   backupService.send = function(data) {
 *     var backupUrl = AuthService.getUrlAstreaPath() + '/api/v2/backup/enqueue'
 *     return $http.post(backupUrl, data);
 *   };
 *
 * `data` shape is `{ id: customer.userId, email: backupEmail }` — ported as
 * `{ userId, email }` (object-argument shape, matching this codebase's usual
 * `mutate({...})` convention) and mapped to the legacy `{id, email}` body
 * here so the wire format is unchanged.
 */
export function useBackupMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async ({ userId, email }) => {
      const response = await httpClient.post(`${baseUrl}/api/v2/backup/enqueue`, {
        id: userId,
        email,
      })
      return response.data
    },
  })
}

/**
 * Port of `BackupService.listByUserId(userId)` + `openBackupHistoryModal(customer)`
 * (commit 585169a, "feat(TA-938): add backup history viewer"):
 *
 *   backupService.listByUserId = function(userId) {
 *     return $http.post('/customers/backup-history', {
 *       userId, urlAstrea: AuthService.getUrlAstrea(), urlAstreaPath: AuthService.getUrlAstreaPath()
 *     });
 *   };
 *
 * Hits THIS repo's `server_app` (`POST /customers/backup-history`,
 * `customerRouter.js`), gated server-side by `allowedToLoadBackup` (a 403 is
 * possible even if the button is only rendered when the client-side flag —
 * `useCurrentUser().allowedToLoadBackup` — allows it; the server re-checks
 * regardless). Implemented as a mutation (not a `useQuery`) to match this
 * file's existing convention for "fetch triggered on demand by a button/
 * modal-open" actions (see `useFirstLoginTokenMutation` above), rather than
 * introducing a second data-fetching pattern for a single on-demand call.
 */
export function useBackupHistoryMutation() {
  return useMutation({
    mutationFn: async (userId) => {
      const response = await httpClient.post('/customers/backup-history', { userId })
      return response.data || []
    },
  })
}

export function useForceFullBackupMutation() {
  return useMutation({
    mutationFn: async (userId) => {
      const response = await httpClient.delete(`/customers/backup-history/${userId}`)
      return response.data
    },
  })
}

export function useResetTwoFactorMutation() {
  return useMutation({
    mutationFn: async (userId) => {
      const response = await httpClient.post(`/customers/two-factor/${userId}/reset`)
      return response.data
    },
  })
}

/**
 * Port of `BankSlipService.updatePendingBankSlips(tenantId)` +
 * `updatePendingBankSlips(tenantId)`:
 *
 *   $http.post(`${urlAstreaPath}/api/v2/bankslip/tenant/${tenantId}/update`, {}, {
 *     headers: { Authorization: AuthService.getServletBasicAuth().replace('Basic ', '') }
 *   });
 *
 * The explicit `Authorization` header is the same fixed servlet Basic Auth
 * value `httpClient`'s request interceptor already attaches to every
 * request (task 008) — not repeated here, same as `useHistoriesQuery`.
 */
export function useUpdatePendingBankSlipsMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async (tenantId) => {
      const response = await httpClient.post(
        `${baseUrl}/api/v2/bankslip/tenant/${tenantId}/update`,
        {},
      )
      return response.data
    },
  })
}

/**
 * Port of `DevService.syncUserPlanWithOroboro(tenantId)` +
 * `TenantService.syncUserCreditsWithRedis(tenantId)`, fired together by
 * `syncUserPlanWithOroboro(tenantId)`:
 *
 *   Promise.all([
 *     DevService.syncUserPlanWithOroboro(tenantId),
 *     TenantService.syncUserCreditsWithRedis(tenantId)
 *   ]).then(() => Materialize.toast(...))
 *   const logToSave = {
 *     namespace: contract.namespace,
 *     oldStatus: contract.planEdition,
 *     newStatus: "SYNC_OROBORO",
 *     who: AuthService.getUserLogin()
 *   };
 *   CustomerService.saveStatusLog(logToSave);
 *
 * NOTE (legacy bug, ported as-is): the log-saving block references a bare
 * `contract` identifier that is not a parameter/closure of
 * `syncUserPlanWithOroboro(tenantId)` (the function only receives
 * `tenantId`) — in AngularJS this throws a `ReferenceError` at runtime
 * (`contract is not defined`) the instant this action is triggered, meaning
 * `saveStatusLog` for this specific action has never actually executed in
 * production, only the two `Promise.all` HTTP calls + the success toast. Per
 * this task's instructions ("mesmo comportamentos que pareçam bugs,
 * documentando-os"): this hook only reproduces the two working requests
 * (Oroboro + Redis credit sync); the always-broken status-log call is
 * deliberately NOT ported, since blindly porting it would either silently
 * "fix" a latent bug (by supplying a valid `contract`) or require inventing
 * behavior. Flagged in this module's "Notas de Execução" instead of guessing.
 */
export function useSyncOroboroMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async (tenantId) => {
      return Promise.all([
        httpClient.post(`${baseUrl}/api/v2/dev/user-plan/tenant/${tenantId}/normalize`, {}),
        httpClient.post(`${baseUrl}/api/v2/tenant/${tenantId}/normalize-credits`, {}),
      ])
    },
  })
}

/**
 * Loads the newest pending contracts for a tenant through THIS repo's
 * `server_app` (`POST /customers/pending-contracts`), matching
 * `useBackupHistoryMutation`'s proxy shape so the backend calls Astrea with
 * the internal script token instead of the browser's default Basic auth.
 */
export function usePendingContractsMutation() {
  return useMutation({
    mutationFn: async (tenantId) => {
      const response = await httpClient.post('/customers/pending-contracts', { tenantId })
      return response.data || []
    },
  })
}

/**
 * Port of `DevService.deleteLawsuitLinkErrors(tenantId)` +
 * `removeSearchResultsWithErrors(tenantId)`:
 *
 *   $http.post(`${urlAstreaPath}/api/v2/dev/lawsuit-link-errors/tenant/${tenantId}/delete`, {}, {...});
 */
export function useRemoveSearchResultsWithErrorsMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async (tenantId) => {
      const response = await httpClient.post(
        `${baseUrl}/api/v2/dev/lawsuit-link-errors/tenant/${tenantId}/delete`,
        {},
      )
      return response.data
    },
  })
}

/**
 * Port of `ElasticService.resetElasticIndex(tenantId, elasticIndexes)` +
 * `resetElasticIndex(tenantId)`:
 *
 *   const payload = { "types": elasticIndexes };
 *   return $http.post(`${urlAstreaPath}/api/v2/elasticsearch/indexes/tenant/${tenantId}/reset`, payload, {...});
 *
 * Takes `{ tenantId, elasticIndexes }` (object-argument shape) instead of
 * positional arguments, matching this codebase's `mutate({...})` convention.
 */
export function useResetElasticIndexMutation() {
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async ({ tenantId, elasticIndexes }) => {
      const response = await httpClient.post(
        `${baseUrl}/api/v2/elasticsearch/indexes/tenant/${tenantId}/reset`,
        { types: elasticIndexes },
      )
      return response.data
    },
  })
}

/** Port of `CustomerService.saveStatusLog(log)`: `POST /statusLogs/create`. */
async function saveStatusLog(log) {
  return httpClient.post('/statusLogs/create', log)
}

/**
 * Port of `createLogForContract(contract, actionName)`, shared by
 * `returnToTrial`/`returnToLight`/`getFirstLoginToken`:
 *
 *   const createLogForContract = (contract, actionName) => ({
 *     namespace: contract.namespace || "AÇÕES DE SUPORTE",
 *     oldStatus: contract.planEdition || "N/A",
 *     newStatus: actionName,
 *     who: AuthService.getUserLogin(),
 *     tenantId: contract.tenantId
 *   });
 */
function createLogForContract(contract, actionName, userLogin) {
  return {
    namespace: contract.namespace || 'AÇÕES DE SUPORTE',
    oldStatus: contract.planEdition || 'N/A',
    newStatus: actionName,
    who: userLogin,
    tenantId: contract.tenantId,
  }
}

/**
 * Port of `CustomerService.changeTenantStatus(tenantId, newStatus, log)`
 * (commit b893e57, "feat(tenant status): permite passar cliente para
 * expirado" — generalized from the earlier TRIAL-only
 * `changeTenantTrial`), shared by `returnToTrial(contract)` and
 * `returnToExpired(contract)`:
 *
 *   customerService.changeTenantStatus = function (tenantId, newStatus, log) {
 *     return $http.post('/customers/change-tenant-status', {
 *       tenantId, status: newStatus, urlAstrea: AuthService.getUrlAstrea(), urlAstreaPath: AuthService.getUrlAstreaPath()
 *     }).then(response => { if (log) customerService.saveStatusLog(log); return response; });
 *   };
 *
 * Hits THIS repo's `server_app` (`POST /customers/change-tenant-status`,
 * `customerRouter.js`), unlike most other actions here — the server proxies
 * to Astrea's internal tenant API with a short-lived script token
 * (`scriptTokenMiddleware`), validating `status` against the same
 * `InternalStatusType` enum (`TRIAL`/`EXPIRED`) declared server-side.
 */
function useChangeTenantStatusMutation(status, logAction) {
  const { getUserLogin } = useAuth()

  return useMutation({
    mutationFn: async (contract) => {
      const response = await httpClient.post('/customers/change-tenant-status', {
        tenantId: contract.tenantId,
        status,
      })
      await saveStatusLog(createLogForContract(contract, logAction, getUserLogin()))
      return response.data
    },
  })
}

/** Port of `returnToTrial(contract)`, see `useChangeTenantStatusMutation` above. */
export function useReturnToTrialMutation() {
  return useChangeTenantStatusMutation('TRIAL', 'RETURN_TO_TRIAL')
}

/**
 * Port of `returnToExpired(contract)` (commit b893e57). Whether a contract
 * is even eligible for this action is a separate, UI-level check
 * (`canReturnToExpired`, ported in `CustomersListPage.jsx`, not here) — this
 * hook only performs the mutation once called.
 */
export function useReturnToExpiredMutation() {
  return useChangeTenantStatusMutation('EXPIRED', 'RETURN_TO_EXPIRED')
}

/**
 * Port of `CustomerService.returnToLight(tenantId, log)` + `returnToLight(contract)`:
 *
 *   customerService.returnToLight = function (tenantId, log) {
 *     return $http.post('/customers/return-to-light-plan', {
 *       tenantId, urlAstrea: AuthService.getUrlAstrea(), urlAstreaPath: AuthService.getUrlAstreaPath()
 *     }).then(response => { if (log) customerService.saveStatusLog(log); return response; });
 *   };
 *
 * Hits THIS repo's `server_app` (`POST /customers/return-to-light-plan`).
 */
export function useReturnToLightMutation() {
  const { getUserLogin } = useAuth()

  return useMutation({
    mutationFn: async (contract) => {
      const response = await httpClient.post('/customers/return-to-light-plan', {
        tenantId: contract.tenantId,
      })
      await saveStatusLog(createLogForContract(contract, 'RETURN_TO_LIGHT', getUserLogin()))
      return response.data
    },
  })
}

/**
 * Port of `CustomerService.getFirstLoginToken(tenantId, log)` +
 * `getFirstLoginToken(contract)`:
 *
 *   customerService.getFirstLoginToken = function (tenantId, log) {
 *     return $http.post('/customers/first-login-token', {
 *       tenantId, urlAstrea: AuthService.getUrlAstrea(), urlAstreaPath: AuthService.getUrlAstreaPath()
 *     }).then(response => { if (log) customerService.saveStatusLog(log); return response; });
 *   };
 *
 * The controller then reads `response.data.activationUrl || response.data`
 * and copies it to the clipboard (`copyContentToClipboard`, ported as a
 * plain `navigator.clipboard.writeText` call in the page component — modern
 * browsers support it directly, unlike the legacy hidden-textarea +
 * `document.execCommand('copy')` fallback, which is deprecated). Hits THIS
 * repo's `server_app` (`POST /customers/first-login-token`).
 */
export function useFirstLoginTokenMutation() {
  const { getUserLogin } = useAuth()

  return useMutation({
    mutationFn: async (contract) => {
      const response = await httpClient.post('/customers/first-login-token', {
        tenantId: contract.tenantId,
      })
      await saveStatusLog(createLogForContract(contract, 'GET_FIRST_LOGIN_TOKEN', getUserLogin()))
      return response.data?.activationUrl || response.data
    },
  })
}

/**
 * Port of `CustomerService.setCleanAccountPermission(profile)` (task 026,
 * `app/customer/editCustomerProfileController.js`'s `editProfile(profile)`):
 *
 *   customerService.setCleanAccountPermission = function(profile) {
 *     var urlAstrea = customerService.getUrlAtrea();
 *     return $http.post(`${urlAstrea}/api/v2/tenant/set-clean-account-permission`, profile)
 *       .then(function(response) {
 *         const log = {
 *           namespace: profile.namespace,
 *           oldStatus: !profile.isCleanAccountAllowed,
 *           newStatus: profile.isCleanAccountAllowed,
 *           who: AuthService.getUserLogin()
 *         }
 *         customerService.saveStatusLog(log);
 *         return response;
 *       });
 *   };
 *
 * `oldStatus`/`newStatus` are booleans (the toggle's state before/after the
 * save), not strings like every other `createLogForContract`-based log in
 * this module — kept as booleans here since that's the literal legacy
 * payload (`!profile.isCleanAccountAllowed` / `profile.isCleanAccountAllowed`),
 * built inline rather than reusing `createLogForContract` (which always
 * stringifies `newStatus`/`oldStatus` to an action name / `"N/A"`, a
 * different log shape entirely).
 */
export function useCustomerProfileMutation() {
  const { getUserLogin } = useAuth()
  const { urlAstreaPath: baseUrl } = useAstreaEnvironment()

  return useMutation({
    mutationFn: async (profile) => {
      const response = await httpClient.post(
        `${baseUrl}/api/v2/tenant/set-clean-account-permission`,
        profile,
      )
      await saveStatusLog({
        namespace: profile.namespace,
        oldStatus: !profile.isCleanAccountAllowed,
        newStatus: profile.isCleanAccountAllowed,
        who: getUserLogin(),
      })
      return response.data
    },
  })
}

/**
 * Port of `CustomerService`-adjacent `app/customer/jwtController.js`
 * (AngularJS `jwtController`, the controller for `jwt.html` — the "Login
 * Administrativo" screen, task 027):
 *
 *   $scope.generate = function () {
 *     $http
 *       .post("/jwt/generate", {
 *         subject: AuthService.getUserLogin(),
 *         urlAstrea: AuthService.getUrlAstrea(),
 *       })
 *       .then(async function (res) {
 *         await navigator.clipboard.writeText(res.data.token);
 *         Materialize.toast("Token gerado e copiado para área de transferência.", 4000);
 *         $scope.token = res.data.token;
 *       })
 *       .catch(function (res) {
 *         Materialize.toast("Ocorreu um erro ao tentar gerar o Token.", 5000);
 *         console.error(res);
 *       });
 *   };
 *
 * Unlike every other hook in this file, `/jwt/generate` is backed by THIS
 * repo's `server_app` (`server_app/routes/jwtRouter.js`), not the external
 * Astrea backend — no `getUrlAstreaPath()` prefix, same bare-path pattern as
 * `saveStatusLog`'s `/statusLogs/create`. The clipboard write + toast side
 * effects are intentionally left to the page component (`../JwtLoginPage.jsx`)
 * to trigger from `onSuccess`, matching how every other mutation in this
 * module keeps side effects (navigation, snackbars) in the page component
 * rather than the hook (see `EditCustomerProfilePage.jsx`'s `handleSave`).
 * `subject`/`urlAstrea` are read from `useAuth()` inside `mutationFn` itself
 * (no arguments needed to `.mutate()`) since the legacy controller never
 * takes any user input for this call either — it always sends the current
 * session's own login/environment.
 */
export function useJwtMutation() {
  const { getUserLogin } = useAuth()

  return useMutation({
    mutationFn: async () => {
      const response = await httpClient.post('/jwt/generate', {
        subject: getUserLogin(),
      })
      return response.data?.token
    },
  })
}
